import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Trash2, Sparkles } from "lucide-react";
import { F, TxtArea } from "../_shared";

export default function StepItinerary({ q, setQ, onAI, aiBusy }) {
  const upd = (i, k, v) => setQ((p) => ({ ...p, itinerary: p.itinerary.map((d, idx) => idx===i ? { ...d, [k]: v } : d) }));
  const add = () => setQ((p) => ({ ...p, itinerary: [...p.itinerary, { day: p.itinerary.length + 1, date: "", city: "", title: "", description: "", meal_plan: "", hotel: "", sightseeing: "", notes: "" }] }));
  const rem = (i) => setQ((p) => ({ ...p, itinerary: p.itinerary.filter((_, idx) => idx !== i).map((d, idx) => ({...d, day: idx+1})) }));

  // Auto-prefill hotel name based on hotels selected (match by city + optional date range).
  useEffect(() => {
    const hotels = (q.hotels || []).filter(h => h && (h.name || h.meal_plan));
    if (!hotels.length || !(q.itinerary || []).length) return;
    const matchHotel = (day) => {
      const city = (day.city || "").toLowerCase().trim();
      if (!city) return null;
      const cands = hotels.filter(h => (h.city || "").toLowerCase().trim() === city);
      if (!cands.length) return null;
      if (cands.length === 1) return cands[0];
      if (day.date) {
        const inRange = cands.find(h => h.check_in && h.check_out && day.date >= h.check_in && day.date < h.check_out);
        if (inRange) return inRange;
      }
      return cands[0];
    };
    const next = q.itinerary.map(d => {
      const h = matchHotel(d);
      if (!h) return d;
      const patch = { ...d };
      if (!(d.hotel && d.hotel.trim()) && h.name) patch.hotel = h.name;
      if (!(d.meal_plan && d.meal_plan.trim()) && h.meal_plan) patch.meal_plan = h.meal_plan;
      return patch;
    });
    const changed = next.some((d, i) => d.hotel !== q.itinerary[i].hotel || d.meal_plan !== q.itinerary[i].meal_plan);
    if (changed) setQ(p => ({ ...p, itinerary: next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify((q.hotels || []).map(h => `${h.city}|${h.name}|${h.meal_plan}|${h.check_in}|${h.check_out}`)),
    JSON.stringify((q.itinerary || []).map(d => `${d.city}|${d.date}`)),
  ]);

  // Return the hotel object that matches a given day (same logic as effect above)
  const hotelForDay = (day) => {
    const hotels = (q.hotels || []).filter(h => h && (h.name || h.meal_plan));
    const city = (day.city || "").toLowerCase().trim();
    if (!city) return null;
    const cands = hotels.filter(h => (h.city || "").toLowerCase().trim() === city);
    if (!cands.length) return null;
    if (cands.length === 1) return cands[0];
    if (day.date) {
      const inRange = cands.find(h => h.check_in && h.check_out && day.date >= h.check_in && day.date < h.check_out);
      if (inRange) return inRange;
    }
    return cands[0];
  };
  const resetMeal = (i) => {
    setQ(p => ({ ...p, itinerary: p.itinerary.map((d, idx) => {
      if (idx !== i) return d;
      const h = hotelForDay(d);
      return { ...d, meal_plan: (h && h.meal_plan) || "" };
    })}));
    toast.success("Meal plan reset to hotel default");
  };

  const [sugCity, setSugCity] = useState((q.cities?.[0]?.city) || "");
  const [sug, setSug] = useState(null);
  const [sugLoading, setSugLoading] = useState(false);
  const [sugTab, setSugTab] = useState("attractions");

  useEffect(() => {
    if (!(q.cities || []).some(c => c.city === sugCity) && q.cities?.length) setSugCity(q.cities[0].city);
    // eslint-disable-next-line
  }, [JSON.stringify((q.cities || []).map(c => c.city))]);

  const loadSuggestions = async (city) => {
    if (!city) return;
    setSugLoading(true); setSug(null);
    try {
      const { data } = await api.post("/ai/city-suggestions", { city, theme: q.trip_theme || "" });
      setSug(data);
    } catch (e) {
      toast.error("Could not load suggestions");
    } finally { setSugLoading(false); }
  };

  const addSuggestion = (item) => {
    setQ((p) => {
      const idx = p.itinerary.findIndex(d => (d.city || "").toLowerCase() === sugCity.toLowerCase());
      const line = item.duration ? `${item.name} (${item.duration})` : item.name;
      if (idx >= 0) {
        const day = p.itinerary[idx];
        const nextSight = day.sightseeing ? `${day.sightseeing}, ${line}` : line;
        const nextDesc = day.description ? `${day.description}\n• ${item.name} — ${item.description || ""}`.trim() : `• ${item.name} — ${item.description || ""}`.trim();
        return { ...p, itinerary: p.itinerary.map((d, i) => i === idx ? { ...d, sightseeing: nextSight, description: nextDesc } : d) };
      }
      return { ...p, itinerary: [...p.itinerary, { day: p.itinerary.length + 1, date: "", city: sugCity, title: item.name, description: item.description || "", meal_plan: "", hotel: "", sightseeing: line, notes: "" }] };
    });
    toast.success(`Added "${item.name}"`);
  };

  return (
    <>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <button data-testid="ai-itin-btn" onClick={onAI} disabled={aiBusy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded bg-[#16A34A] text-white text-sm hover:bg-emerald-700 disabled:opacity-60">
          <Sparkles size={14} /> {aiBusy ? "Generating…" : "✨ Generate AI Itinerary"}
        </button>
        <button data-testid="itin-add-btn" onClick={add} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#16A34A]"><Plus size={14} /> Add day</button>
        <label className="ml-auto text-xs flex items-center gap-2">
          <span className="uppercase tracking-widest text-slate-500">Trip Theme</span>
          <select data-testid="trip-theme" value={q.trip_theme || ""} onChange={(e) => setQ((p) => ({ ...p, trip_theme: e.target.value }))} className="border border-border rounded px-2 py-1.5 text-sm bg-white">
            <option value="">— None —</option>
            {["Family","Honeymoon","Adventure","Luxury","Group","Senior Citizen","Corporate"].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
      </div>

      {(q.cities?.length || 0) > 0 && (
        <div data-testid="city-suggestions-panel" className="bg-emerald-50 border border-emerald-200 rounded p-4 mb-5">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="text-sm font-semibold text-emerald-900">City suggestions</div>
            <div className="text-xs text-emerald-800/80 flex-1">Pick a city and add attractions in one click.</div>
            <select data-testid="sug-city-select" value={sugCity} onChange={(e) => setSugCity(e.target.value)} className="border border-emerald-300 rounded px-2 py-1.5 text-sm bg-white">
              {(q.cities || []).map((c, i) => <option key={i} value={c.city}>{c.city}</option>)}
            </select>
            <button data-testid="sug-load-btn" onClick={() => loadSuggestions(sugCity)} disabled={sugLoading || !sugCity} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded disabled:opacity-60">
              {sugLoading ? "Loading…" : (sug ? "Refresh" : "Load suggestions")}
            </button>
          </div>
          {sug && (
            <>
              <div className="flex gap-1 mb-3">
                {["attractions","sightseeing","activities"].map((k) => (
                  <button key={k} data-testid={`sug-tab-${k}`} onClick={() => setSugTab(k)} className={`text-xs px-3 py-1.5 rounded-full ${sugTab === k ? "bg-[#0F1E5B] text-white" : "bg-white border border-emerald-200 text-slate-700"}`}>
                    {k.charAt(0).toUpperCase() + k.slice(1)} · {sug[k]?.length || 0}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(sug[sugTab] || []).map((it, idx) => (
                  <div key={idx} data-testid={`sug-card-${sugTab}-${idx}`} className="bg-white rounded p-3 border border-emerald-100">
                    <div className="text-sm font-semibold text-slate-900 leading-tight">{it.name}</div>
                    <div className="flex items-center gap-2 mt-1 text-[10px] uppercase tracking-widest text-emerald-700">
                      {it.category && <span>{it.category}</span>}
                      {it.duration && <span className="text-slate-400">· {it.duration}</span>}
                    </div>
                    <div className="text-xs text-slate-600 mt-1.5 line-clamp-2 min-h-[32px]">{it.description}</div>
                    <button data-testid={`sug-add-${sugTab}-${idx}`} onClick={() => addSuggestion(it)} className="mt-2 w-full inline-flex items-center justify-center gap-1 text-xs bg-[#16A34A] hover:bg-emerald-700 text-white px-2 py-1.5 rounded">
                      <Plus size={11} /> Add to itinerary
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className="space-y-4">
        {q.itinerary.map((d, i) => (
          <div key={i} className="border border-border rounded p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="overline text-[10px]">Day {d.day}</div>
              <button data-testid={`itin-rem-${i}`} onClick={()=>rem(i)} className="text-red-500"><Trash2 size={14} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Date" type="date" value={d.date} onChange={(v)=>upd(i, "date", v)} testid={`itin-date-${i}`} />
              <F label="City" value={d.city} onChange={(v)=>upd(i, "city", v)} testid={`itin-city-${i}`} />
              <div className="col-span-2">
                <F label="Title" value={d.title} onChange={(v)=>upd(i, "title", v)} testid={`itin-title-${i}`} />
              </div>
              <div className="col-span-2">
                <TxtArea label="Description" value={d.description} onChange={(v)=>upd(i, "description", v)} testid={`itin-desc-${i}`} rows={3} />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5 flex items-center justify-between">
                  <span>Meal Plan</span>
                  {(() => {
                    const h = hotelForDay(d);
                    const defaultPlan = (h && h.meal_plan) || "";
                    const differs = (d.meal_plan || "") !== defaultPlan;
                    if (!defaultPlan || !differs) return null;
                    return (
                      <button
                        type="button"
                        data-testid={`itin-meal-reset-${i}`}
                        onClick={() => resetMeal(i)}
                        className="text-[10px] normal-case tracking-normal text-emerald-700 hover:text-emerald-800 hover:underline"
                        title={`Reset to hotel default (${defaultPlan})`}
                      >
                        ↺ Reset to hotel default
                      </button>
                    );
                  })()}
                </label>
                <select
                  data-testid={`itin-meal-${i}`}
                  value={d.meal_plan || ""}
                  onChange={(e) => upd(i, "meal_plan", e.target.value)}
                  className="w-full border border-border rounded px-3 py-2 text-sm bg-white"
                >
                  <option value="">— None —</option>
                  <option value="RO">Room Only (RO)</option>
                  <option value="CP">Breakfast (CP)</option>
                  <option value="BB">Breakfast (BB)</option>
                  <option value="MAP">Half Board (MAP)</option>
                  <option value="HB">Half Board (HB)</option>
                  <option value="AP">Full Board / All Meals (AP)</option>
                  <option value="FB">Full Board (FB)</option>
                </select>
              </div>
              <F label="Hotel" value={d.hotel} onChange={(v)=>upd(i, "hotel", v)} testid={`itin-hotel-${i}`} />
              <F label="Sightseeing" value={d.sightseeing} onChange={(v)=>upd(i, "sightseeing", v)} testid={`itin-sight-${i}`} />
              <F label="Notes" value={d.notes} onChange={(v)=>upd(i, "notes", v)} testid={`itin-notes-${i}`} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
